// Compress a user-picked image into a JPEG suitable for an event
// attachment (concert ticket, school flyer, vet receipt). Larger than
// avatar compression — receipts and flyers need to stay readable —
// but still aggressive enough to fit comfortably under the 350KB
// server limit even after base64 bloat.
//
// Steps:
//   1. Read the File into an Image via createObjectURL.
//   2. Scale the longer edge to TARGET_LONG_EDGE, keeping aspect ratio.
//   3. Draw onto an offscreen canvas.
//   4. Export as JPEG at JPEG_QUALITY → data URL.
//
// Returns a Promise<string> resolving to a `data:image/jpeg;base64,...` URL.

const TARGET_LONG_EDGE = 1280;
const JPEG_QUALITY = 0.78;
// Generous base64 cap (server enforces 350,000 chars). If the picked
// image still exceeds this after compression, we re-encode at a
// lower quality once before giving up.
const MAX_DATA_URL_LEN = 320_000;

export async function compressEventAttachmentFile(file) {
  if (!file || !file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  if (file.size > 15 * 1024 * 1024) {
    throw new Error("Image is too big — try one under 15MB.");
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const longEdge = Math.max(img.naturalWidth, img.naturalHeight) || TARGET_LONG_EDGE;
    const scale = Math.min(1, TARGET_LONG_EDGE / longEdge);
    const targetW = Math.max(1, Math.round(img.naturalWidth * scale));
    const targetH = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, targetW, targetH);
    let dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    // Re-encode at a lower quality if still over the cap (very large
    // photos with lots of detail). One retry is enough in practice.
    if (dataUrl.length > MAX_DATA_URL_LEN) {
      dataUrl = canvas.toDataURL("image/jpeg", 0.6);
    }
    if (!dataUrl || !dataUrl.startsWith("data:image/")) {
      throw new Error("Could not read this image — try a different one.");
    }
    return dataUrl;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't open this image."));
    img.src = src;
  });
}
